"use client";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { api } from "@/lib/api";
import { usePendingPoll } from "@/lib/usePendingPoll";
import { Toast, SpinnerIcon, WarningIcon } from "@/components/Toast";
import {
  getPendingGeneration, setPendingGeneration, clearPendingGeneration, PENDING_GENERATION_EVENT,
  type PendingGeneration,
} from "@/lib/pendingGeneration";

// The custom-patient pipeline runs in the background (Karar 40): the create
// page kicks it off and drops the student on the dashboard, so THIS is what
// tells them what happened. Same toast grammar as PendingEvalWatcher, at the
// BOTTOM-right so the two toasts never sit on top of each other:
//   1. "running" : the patient is being written (~2 minutes)
//   2. "ready"   : done, with a button to the dashboard's case list
// plus a terminal "failed" (retry; the monthly quota was NOT spent).

type Kind = "running" | "ready" | "failed";

// Auto-dismiss per beat, mirroring PendingEvalWatcher's reasoning: "ready" is
// the clickable beat and stays longest; "failed" waits for the student, it is
// their only handle on a generation that did not happen.
const AUTO_DISMISS_MS: Record<Kind, number | null> = {
  running: 8000,
  ready: 12000,
  failed: null,
};
const TERMINAL: readonly Kind[] = ["failed"];

const identity = (e: PendingGeneration) => e.startedAt;
const isReady = (e: PendingGeneration) => !!e.ready;
const EVENTS = [PENDING_GENERATION_EVENT] as const;

export function PendingGenerationWatcher() {
  const t = useTranslations("generationToast");
  const router = useRouter();

  const { entry, kind, visible, dismissBeat, end } = usePendingPoll<PendingGeneration, Kind>({
    read: getPendingGeneration,
    identity,
    isReady,
    readyBeat: "ready",
    runningBeat: "running",
    terminalBeats: TERMINAL,
    clear: clearPendingGeneration,
    events: EVENTS,
    deps: [router],
    poll: async (pending) => {
      const data = await api.getGenerationStatus();
      if (data.status === "ready") {
        setPendingGeneration({ ...pending, ready: true, caseName: data.name ?? null });
        // The dashboard may already be on screen: make the new case appear.
        router.refresh();
        return { beat: "ready" };
      }
      if (data.status === "failed") {
        clearPendingGeneration();
        return { beat: "failed" };
      }
      if (data.status === "none") {
        // "none" = the entry outlived its blueprint (account cleanup etc.).
        return { drop: true };
      }
      return { again: true };
    },
  });

  if (!visible || !entry || !kind) return null;

  function openTarget() {
    end();
    router.push(kind === "failed" ? "/create-patient" : "/dashboard");
  }

  const name = entry.caseName || entry.requestedName || null;
  const body = name ? t(`${kind}BodyWith`, { name }) : t(`${kind}Body`);
  const cta = kind === "ready" ? t("readyCta") : kind === "failed" ? t("failedCta") : null;

  const icon = kind === "running" ? <SpinnerIcon /> : kind === "ready" ? (
    // Person silhouette: the beat that delivers a new patient.
    <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
    </svg>
  ) : <WarningIcon />;

  return (
    <Toast
      position="bottom"
      beatKey={kind}
      tone={kind === "failed" ? "warn" : "accent"}
      icon={icon}
      title={t(`${kind}Title`)}
      body={body}
      cta={cta}
      onCta={openTarget}
      autoDismissMs={AUTO_DISMISS_MS[kind]}
      onClose={dismissBeat}
      dismissLabel={t("dismiss")}
      autoCloseLabel={(seconds) => t("autoClose", { seconds })}
    />
  );
}
